import { createFileRoute, redirect } from "@tanstack/react-router";

import { currentUserQueryOptions } from "@/hooks/current-user";
import { loginWithReturnTo } from "@/lib/route-paths";

// Pathless layout guarding every authenticated route: what's protected is
// decided by membership in this subtree (type-enforced by the route tree),
// not by a hand-kept path allowlist. Public routes (/login, the /api/auth/*
// server routes) simply live outside it.
//
// The current-user lookup goes through the Query cache (fetchQuery +
// staleTime in currentUserQueryOptions), so hover-intent preloads and
// navigations within the staleTime window don't re-fire the auth RPC.
// fetchQuery — not ensureQueryData — is load-bearing here: past the
// staleTime window it refetches *before* gating, whereas ensureQueryData
// returns the cached user forever (even revalidateIfStale only refreshes
// in the background, after the stale user has already passed the gate).
//
// A preload is the exception, and reads the cache as it stands: it renders
// nothing and gates nothing, and the navigation it precedes runs this gate
// again, refetching a stale user before letting it through. So a control that
// preloads before it pushes — the spec Version dropdown (#714) — never waits
// an auth round trip for the push. It still starts the refetch
// (revalidateIfStale), as the fetch it replaces did, so the navigation that
// follows a hover joins it in flight or finds it done.
export const Route = createFileRoute("/_authed")({
  // Gate only — deliberately no component. The chrome rides the _chrome
  // subtree one level down (routes/_authed/_chrome.tsx), so the catch-all's
  // 404 can stay chrome-less beside it while every page renders inside the
  // header.
  beforeLoad: async ({ context, location, preload }) => {
    const currentUser = preload
      ? await context.queryClient.ensureQueryData({
          ...currentUserQueryOptions(),
          revalidateIfStale: true,
        })
      : await context.queryClient.fetchQuery(currentUserQueryOptions());
    if (!currentUser) {
      throw redirect(loginWithReturnTo(location.href));
    }
    // Named for the shape it is: descendants narrow it themselves
    // (`currentUser.user` for identity, `currentUser.session` for grants).
    return { currentUser };
  },
});
